import { AnyAction } from 'redux';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching, LoadOptions } from 'app/types-shared';
import { SuccessNotification, ErrorNotification, InfoNotification } from 'app/infrastructure/notifications/user-messages';
import { api } from './api';
import { sharedApi } from '../shared/api';
import {
  ClearList, RequestList, ReceiveList, FailList,
  RequestDelete, ReceiveDelete, FailDelete,
  RequestPersonAccess, ReceivePersonAccess, FailPersonAccess, ClearPersonAccess,
  RequestUserExportExcel, ReceiveUserExportExcel, FailUserExportExcel,
  RequestRoleExportExcel, ReceiveRoleExportExcel, FailRoleExportExcel,
  RequestDefaultRole, ReceiveDefaultRole, FailDefaultRole,
  RequestSetDefaultRole, ReceiveSetDefaultRole, FailSetDefaultRole,
  RequestExtraInfo, ReceiveExtraInfo, FailExtraInfo,
  RequestCheckRoles, ReceiveCheckRoles, FailCheckRoles,
  UpdateMergeUser,
  RequestUserOptions,
  ReceiveUserOptions,
  FailUserOptions,
  RequestUserItemDelete,
  ReceiveUserItemDelete,
  FailUserItemDelete,
  UpdateValidation,
  ResetValidation
} from './action-types';
import { FilterSearchModel } from 'app/area/search-filters/types-store';
import { SearchTranslation, searchTranslations as text } from './constants';
import { getUserMessage } from 'app/infrastructure/notifications/user-messages/get-user-message';
import { downloadFile } from 'app/infrastructure/platform/file';
import { isForbiddenStatus, blockAccess } from 'app/area/shared/actions-utils';
import { STATE } from 'app/constants';
import { buildDatedFilename } from 'app/infrastructure/filename/build-dated-filename';
import { DefaultRole } from 'app/area/administration/shared/types';
import { mapFromDefaultRole, mapToDefaultRole } from 'app/area/administration/edit/user/default-role-mapper';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { DeleteUserItemPayload, MergeUserState } from 'app/area/administration/shared/merge-user/types-store';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { userOptionsMapped } from 'app/area/administration/shared/merge-user/mapping';

export const actionCreators = {

  clearPersonAccess: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {
      dispatch(new ClearPersonAccess());
    },

  loadUserAccess: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {
      dispatch(new RequestPersonAccess());

      try {
        const userAccess = await sharedApi.userAccess(id);

        dispatch(new ReceivePersonAccess(userAccess[0]));
      } catch (err) {
        dispatch(new FailPersonAccess(id));
      }
    },

  loadRoleAccess: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {
      dispatch(new RequestPersonAccess());

      try {
        const userAccess = await sharedApi.roleAccess(id);
        dispatch(new ReceivePersonAccess(userAccess[0]));
      } catch (err) {
        dispatch(new FailPersonAccess(id));
      }
    },

  clearList: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {

      const requestId = Date.now();
      dispatch(new ClearList(requestId));
    },

  loadUsers: (model: FilterSearchModel, startIndex: number = 0, maxResults: number = 20): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const requestId = Date.now();
      dispatch(new RequestList(requestId, model, startIndex === 0));

      try {
        const users = await api.getUsers(startIndex, maxResults, model);

        dispatch(new ReceiveList(requestId, users.data.results, users.data.totalNumberOfResults));
      } catch (err) {
        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationSearch,
            translationId: text.no_access_to_user_administration
          });
        }

        dispatch(new FailList(requestId));
      }
    },

  deleteUser: (id: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestDelete(id));

      try {
        await sharedApi.deleteUser(id);

        dispatch(new ReceiveDelete(id));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));
        dispatch(new FailDelete(id));
      }
    },

  userExportToExcel: (model: FilterSearchModel, maxResults: number, startMessage: string, successMessage: string):
    ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {
      const startIndex = 0;

      dispatch(new RequestUserExportExcel());
      dispatch(new InfoNotification(startMessage));

      try {
        const response = await api.userExportToExcel(startIndex, maxResults, model);
        downloadFile(response.blob, buildDatedFilename('export-user-list', 'xlsx'));
        dispatch(new SuccessNotification(successMessage));
        dispatch(new ReceiveUserExportExcel());

      } catch (err) {
        dispatch(new FailUserExportExcel());
        err && err.userMessages && dispatch(new ErrorNotification(getUserMessage(err.userMessages)));
      }
    },

  roleExportToExcel: (model: FilterSearchModel, maxResults: number, startMessage: string, successMessage: string):
    ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {
      const startIndex = 0;

      dispatch(new RequestRoleExportExcel());
      dispatch(new InfoNotification(startMessage));

      try {
        const response = await api.roleExportToExcel(startIndex, maxResults, model);
        downloadFile(response.blob, buildDatedFilename('export-role-list', 'xlsx'));

        dispatch(new SuccessNotification(successMessage));
        dispatch(new ReceiveRoleExportExcel());

      } catch (err) {
        dispatch(new FailRoleExportExcel());
        err && err.userMessages && dispatch(new ErrorNotification(getUserMessage(err.userMessages)));
      }
    },

  loadRoles: (model: FilterSearchModel, startIndex: number = 0,
    maxResults: number = 20): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const requestId = Date.now();
      dispatch(new RequestList(requestId, model, startIndex === 0));

      try {
        const roles = await api.getRoles(startIndex, maxResults, model);

        dispatch(new ReceiveList(requestId, roles.data.results, roles.data.totalNumberOfResults));
      } catch (err) {
        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationSearch,
            translationId: text.no_access_to_role_administration
          });
        }

        dispatch(new FailList(requestId));
      }
    },

  deleteRole: (id: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestDelete(id));

      try {
        await sharedApi.deleteRole(id);

        dispatch(new ReceiveDelete(id));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));
        dispatch(new FailDelete(id));
      }
    },

  loadDefaultRole: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new RequestDefaultRole());
      try {
        const defaultRole = await sharedApi.getDefaultRole();
        dispatch(new ReceiveDefaultRole(mapFromDefaultRole(defaultRole)));
      } catch (err) {
        dispatch(new FailDefaultRole());
      }
    },

  setDefaultRole: (defaultRole: DefaultRole, translation: SearchTranslation): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const isRemovingDefaultRole = !isDefinedIncludingZero(defaultRole?.value);
      dispatch(new RequestSetDefaultRole(defaultRole?.value));
      try {
        await sharedApi.setDefaultRole(mapToDefaultRole(defaultRole));
        dispatch(new ReceiveSetDefaultRole());
        isRemovingDefaultRole
          ? dispatch(new SuccessNotification(translation.remove_default_role_success))
          : dispatch(new SuccessNotification(translation.mark_as_default_role_success));
      } catch (err) {
        isRemovingDefaultRole
          ? dispatch(new ErrorNotification(translation.remove_default_role_failure))
          : dispatch(new ErrorNotification(translation.mark_as_default_role_failure));
        dispatch(new FailSetDefaultRole());
      }
    },

  loadExtraInfo: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new RequestExtraInfo());
      try {
        const extraInfo = await api.getExtraInfo();
        dispatch(new ReceiveExtraInfo(extraInfo?.data));
      } catch (err) {
        dispatch(new FailExtraInfo());
      }
    },

  checkRoles: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new RequestCheckRoles());
      try {
        const roles = await sharedApi.getRoles();
        dispatch(new ReceiveCheckRoles(roles.length > 0));
      } catch (err) {
        dispatch(new FailCheckRoles());
      }
    },

  updateMergeUser: (mergeUser: MergeUserState): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new UpdateMergeUser(mergeUser));
    },

  loadUserOptions: (loadOptionsParams: LoadOptions): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new RequestUserOptions());

      try {
        const userOptions = await sharedApi.getUsers(loadOptionsParams);
        dispatch(new ReceiveUserOptions(userOptionsMapped(userOptions.data.results)));
      } catch (err) {
        dispatch(new FailUserOptions());
      }
    },

  deleteMergeUser: (payload: DeleteUserItemPayload): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const { id, successMessage, reassignReferencesToUser } = payload;
      dispatch(new RequestUserItemDelete(id));

      try {
        await sharedApi.deleteMergeUser(id, reassignReferencesToUser);

        dispatch(new ReceiveUserItemDelete(id));
        dispatch(new UpdateMergeUser(undefined));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        dispatch(new FailUserItemDelete(id));

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new UpdateValidation(complexValidationFromServer(err.validationErrors)));
        }
      }
    },

  resetValidation: (key: string): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(key));
  },

  displayError: (errorMessage: string): ActionDispatching<any, any> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
